/**
 * Copyright (c) 2025, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { CartonAuditService, CartonAuditService_StateInfoDto, CartonAuditService_StateEnum } from "app/services/carton-audit.service";
import { CookieService } from "ngx-cookie-service";

const CookieKey_CartonAuditSelectedService = "page[carton-audit-portal]-key[selected-service-id]";

@Component({
  selector: 'app-carton-audit-select-location',
  templateUrl: './carton-audit-select-location.component.html',
  styleUrls: ['./carton-audit-select-location.component.css']
})
export class CartonAuditSelectLocationComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Services available for the User to select */
  availableServices: ServiceInstance[] = [];

  /** Currently selected Service in the UI */
  selectedService: ServiceInstance = null;

  /** Flag used to disable the UI while performing background tasks */
  isUiDisabled: boolean = false;

  constructor(
    private cartonAuditService: CartonAuditService,
    private cookieService: CookieService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
  console.log("Initializing Carton Audit - Select Service Component");

  this.route.data.subscribe((data: { availableServices: ServiceInstance[] }) => {
    console.log("Resolved Carton Audit services:", data.availableServices);
    this.setAvailableServices(data.availableServices);
  });
}

  private async setAvailableServices(services: ServiceInstance[]): Promise<void> {
  this.availableServices = services ?? [];

  const previouslySelectedServiceId = this.getStoredServiceId();
  this.selectedService = this.availableServices.find(
    s => s?.id === previouslySelectedServiceId
  ) ?? null;

  console.log("Available after resolver:", this.availableServices);
}

  private getStoredServiceId(): number {
    const value = this.cookieService.get(CookieKey_CartonAuditSelectedService);
    if (!value) return null;
    try {
      return JSON.parse(value).service_id;
    } catch {
      return null;
    }
  }

  private setStoredServiceId(id: number): void {
    const value = { service_id: id };
    this.cookieService.set(CookieKey_CartonAuditSelectedService, JSON.stringify(value), 1, '/', null, false, 'Lax');
  }

  onSelectServiceClicked(): void {
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.selectService();
    }
  }

  private async selectService(): Promise<void> {
    if (this.selectedService?.id != null) {
      this.setStoredServiceId(this.selectedService.id);

      let currentState: CartonAuditService_StateInfoDto = null;
      try {
        currentState = await this.cartonAuditService.getCurrentState(this.selectedService.id);
      } catch (error) {
        this.handleError(error);
        this.isUiDisabled = false;
        return;
      }

      if (currentState?.state != null) {
        switch (currentState.state) {
          case CartonAuditService_StateEnum.EnterCartonId:{
            //this.router.navigate(['/jockey/carton-audit/dashboard', this.serviceInstance!.id]);
            this.router.navigate(['jockey','carton-audit', this.selectedService.id, 'enter-info']);
            return;
          }
          case CartonAuditService_StateEnum.CartonBeingAudited: {
           this.router.navigate(['jockey','carton-audit', this.selectedService.id, 'dashboard']);
            return;
          }
          default: {
            this.handleError({ error: `Unhandled State: '${currentState.state}'...`});
            this.isUiDisabled = false;
            return;
          }
        }
      } else {
        this.handleError({ error: "No Audit State obtained!"});
        this.isUiDisabled = false;
        return;
      }
    } else {
      this.setStoredServiceId(null);
      this.isUiDisabled = false;
    }
  }

  handleError(error: any): void {
    console.error("Error detected: " + error);
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } else if (error != null) {
      alert(error?.text ? error.text() : error);
    }
  }
}

interface StoredServiceId {
  service_id: number;
}
